import { ParsedCoordinatePair } from '../models/coordinate-search.model';

const DMS_TOKEN =
  /^(\d{1,3})°\s*(\d{1,2})['′]\s*(\d{1,2}(?:\.\d+)?)["″]\s*([NnSsEeWw])$/;

function splitPair(input: string): [string, string] | null {
  const commaSplit = input.split(',').map((t) => t.trim()).filter(Boolean);
  if (commaSplit.length === 2) {
    return [commaSplit[0], commaSplit[1]];
  }

  const whitespaceSplit = input.split(/\s+/).filter(Boolean);
  if (whitespaceSplit.length === 2) {
    return [whitespaceSplit[0], whitespaceSplit[1]];
  }

  return null;
}

function dmsTokenToDecimal(deg: number, min: number, sec: number, hemisphere: string): number {
  const sign = hemisphere.toUpperCase() === 'S' || hemisphere.toUpperCase() === 'W' ? -1 : 1;
  const abs = Math.abs(deg) + min / 60 + sec / 3600;
  return sign * abs;
}

/** DMS: 45°30'6"N 73°34'2"W */
export function parseDmsPair(input: string): ParsedCoordinatePair | null {
  const trimmed = input.trim();
  const pair = splitPair(trimmed);
  if (!pair) {
    return null;
  }
  const [latToken, lngToken] = pair;

  const latMatch = latToken.match(DMS_TOKEN);
  const lngMatch = lngToken.match(DMS_TOKEN);
  if (!latMatch || !lngMatch) {
    return null;
  }

  if (!/[NnSs]/.test(latMatch[4]) || !/[EeWw]/.test(lngMatch[4])) {
    return null;
  }

  const latDeg = Number(latMatch[1]);
  const latMin = Number(latMatch[2]);
  const latSec = Number(latMatch[3]);
  const lngDeg = Number(lngMatch[1]);
  const lngMin = Number(lngMatch[2]);
  const lngSec = Number(lngMatch[3]);

  if (
    [latDeg, latMin, latSec, lngDeg, lngMin, lngSec].some((n) => !Number.isFinite(n)) ||
    latMin >= 60 ||
    latSec >= 60 ||
    lngMin >= 60 ||
    lngSec >= 60
  ) {
    return null;
  }

  const lat = dmsTokenToDecimal(latDeg, latMin, latSec, latMatch[4]);
  const lng = dmsTokenToDecimal(lngDeg, lngMin, lngSec, lngMatch[4]);

  return {
    lat,
    lng,
    sourceFormat: 'dms',
    rawInput: input,
  };
}
